package org.teddapp.project.ui.finance

import org.teddapp.project.ui.TeddSize

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.tedd.db.Asset
import com.tedd.db.Asset_valuation
import com.tedd.db.Finance_account
import com.tedd.db.Loan
import com.tedd.db.Loan_payment
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddCard
import org.teddapp.project.ui.TeddDateField
import org.teddapp.project.ui.TeddDates
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddIconButton
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType

/** Nature d'un bien. L'icône et la couleur suivent, rien n'est saisi deux fois. */
internal enum class AssetKind(val key: String, val label: String, val icon: String, val hex: String) {
    RealEstate("realestate", "Immobilier", "home", "#22508A"),
    Land("land", "Terrain", "flag", "#2D7A4F"),
    Stocks("stocks", "Bourse", "cards", "#0F7C8C"),
    Gold("gold", "Or et métaux", "spark", "#B8892C"),
    Vehicle("vehicle", "Véhicule", "grid", "#6F7D8C"),
    Other("other", "Autre", "wallet", "#9A6B3A");

    companion object {
        fun of(key: String?): AssetKind = entries.firstOrNull { it.key == key } ?: Other
    }
}

/** Plus-value d'un bien : valeur constatée moins prix payé, ou null si l'un manque. */
internal fun assetGain(asset: Asset): Long? {
    val v = asset.value_ ?: return null
    val p = asset.purchase_price ?: return null
    return v - p
}

/**
 * Patrimoine : les biens qui ne sont pas des comptes. On constate leur valeur, on ne les
 * débite pas. Rien n'est rouge et rien ne clignote : une valeur ancienne se signale en gris.
 */
@Composable
internal fun PatrimoineTab(
    assets: List<Asset>,
    valuations: List<Asset_valuation>,
    accounts: List<Finance_account>,
    loans: List<Loan>,
    loanPayments: List<Loan_payment>,
    hideBalance: Boolean,
    compact: Boolean,
    onCreate: () -> Unit,
    onEdit: (Asset) -> Unit,
    onValue: (Asset) -> Unit,
    onDelete: (Asset) -> Unit,
    onCreateLoan: () -> Unit,
    onEditLoan: (Loan) -> Unit,
    onRepayLoan: (Loan) -> Unit,
    onLoanPayments: (Loan) -> Unit,
    onDeleteLoan: (Loan) -> Unit,
    modifier: Modifier = Modifier,
) {
    val live = assets.filter { it.is_deleted == 0L && it.archived == 0L }
    val mainCurrency = accounts.firstOrNull()?.currency ?: "XOF"
    val accountsTotal = accounts.filter { it.is_deleted == 0L && it.currency == mainCurrency }.sumOf { it.balance }
    val assetsTotal = live.filter { it.currency == mainCurrency }.sumOf { it.value_ ?: 0L }
    val loanTotals = loanTotals(loans, loanPayments, mainCurrency)
    val invested = live.filter { it.currency == mainCurrency }.sumOf { it.purchase_price ?: 0L }
    val gain = live.mapNotNull { assetGain(it) }.sum()
    val lastValued = live.mapNotNull { it.valued_at }.maxOrNull()
    val h = if (compact) 16.dp else 28.dp

    Column(
        modifier = modifier.fillMaxSize().verticalScroll(rememberScrollState()).padding(start = h, end = h, top = 14.dp, bottom = 36.dp),
        verticalArrangement = Arrangement.spacedBy(14.dp),
    ) {
        // Trois nombres : net, plus-value, dernier constat.
        if (compact) {
            NetWorthCard(accountsTotal, assetsTotal, loanTotals, mainCurrency, hideBalance)
            Row(horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                GainCard(gain, invested, mainCurrency, hideBalance, Modifier.weight(1f))
                LastValuedCard(lastValued, Modifier.weight(1f))
            }
        } else {
            Row(horizontalArrangement = Arrangement.spacedBy(14.dp)) {
                NetWorthCard(accountsTotal, assetsTotal, loanTotals, mainCurrency, hideBalance, Modifier.weight(1.4f))
                GainCard(gain, invested, mainCurrency, hideBalance, Modifier.weight(1f))
                LastValuedCard(lastValued, Modifier.weight(1f))
            }
        }

        Row(verticalAlignment = Alignment.CenterVertically) {
            Text("MES BIENS", style = TeddType.Overline, color = TeddTokens.InkMuted, modifier = Modifier.weight(1f))
            TeddButton(
                text = "Ajouter un bien",
                onClick = onCreate,
                kind = TeddButtonKind.Secondary,
                size = TeddButtonSize.Sm,
                leadingIcon = { TeddIcon(name = "plus", size = 12.dp, color = TeddTokens.Ink) },
            )
        }

        if (live.isEmpty()) {
            TeddCard(modifier = Modifier.fillMaxWidth(), padding = PaddingValues(20.dp)) {
                Text("Aucun bien déclaré", style = TeddType.Heading, color = TeddTokens.Ink)
                Text(
                    text = "Une maison, un terrain, de l'or, un portefeuille : ces choses ont une valeur qui change, " +
                        "pas un solde qui bouge à chaque dépense. Déclare-les ici et constate leur valeur quand tu la connais.",
                    style = TeddType.BodyLg,
                    color = TeddTokens.InkMuted,
                    modifier = Modifier.padding(top = 8.dp),
                )
            }
        } else {
            live.forEach { asset ->
                AssetRow(
                    asset = asset,
                    valuationCount = valuations.count { it.asset_id == asset.id && it.is_deleted == 0L },
                    hideBalance = hideBalance,
                    onEdit = { onEdit(asset) },
                    onValue = { onValue(asset) },
                    onDelete = { onDelete(asset) },
                )
            }
        }

        // Les prêts : ce qu'on me doit et ce que je dois, en plus des comptes et des biens.
        Spacer(modifier = Modifier.height(6.dp))
        LoanTotalsCards(loanTotals, mainCurrency, hideBalance, compact)
        LoansSection(
            loans = loans,
            payments = loanPayments,
            hideBalance = hideBalance,
            compact = compact,
            onCreate = onCreateLoan,
            onEdit = onEditLoan,
            onRepay = onRepayLoan,
            onPayments = onLoanPayments,
            onDelete = onDeleteLoan,
        )
    }
}

@Composable
private fun NetWorthCard(accountsTotal: Long, assetsTotal: Long, loans: LoanTotals, currency: String, hide: Boolean, modifier: Modifier = Modifier) {
    // Le net compte les créances qui restent à recevoir et retire les dettes qui restent à payer.
    val net = accountsTotal + assetsTotal + loans.receivable - loans.debt
    TeddCard(modifier = modifier.fillMaxWidth(), padding = PaddingValues(18.dp)) {
        Text("PATRIMOINE NET", style = TeddType.Overline, color = TeddTokens.InkMuted)
        Text(
            text = if (hide) "•••• $currency" else fmtMoney(net, currency, compact = false),
            style = TeddType.Numeral.copy(fontSize = 32.sp, lineHeight = 38.sp),
            color = TeddTokens.Ink,
            modifier = Modifier.padding(top = 6.dp),
        )
        Text(
            text = if (hide) "comptes, biens et prêts" else buildString {
                append("comptes ${fmtMoney(accountsTotal, currency, compact = true)} · biens ${fmtMoney(assetsTotal, currency, compact = true)}")
                if (loans.receivable > 0L) append(" · on me doit ${fmtMoney(loans.receivable, currency, compact = true)}")
                if (loans.debt > 0L) append(" · je dois ${fmtMoney(loans.debt, currency, compact = true)}")
            },
            color = TeddTokens.InkMuted,
            fontSize = 12.sp,
            modifier = Modifier.padding(top = 4.dp),
        )
    }
}

@Composable
private fun GainCard(gain: Long, invested: Long, currency: String, hide: Boolean, modifier: Modifier = Modifier) {
    TeddCard(modifier = modifier.fillMaxWidth(), padding = PaddingValues(18.dp)) {
        Text("PLUS-VALUE", style = TeddType.Overline, color = TeddTokens.InkMuted)
        Text(
            text = when {
                hide -> "••••"
                invested == 0L -> "—"
                else -> (if (gain >= 0) "+" else "−") + fmtMoney(if (gain < 0) -gain else gain, currency, compact = true)
            },
            style = TeddType.Numeral.copy(fontSize = 26.sp, lineHeight = 32.sp),
            color = if (invested == 0L || hide) TeddTokens.Ink else if (gain >= 0) TeddTokens.Success else TeddTokens.Warning,
            modifier = Modifier.padding(top = 6.dp),
        )
        Text(
            text = if (invested == 0L) "aucun prix d'achat renseigné" else "sur ${fmtMoney(invested, currency, compact = true)} investis",
            color = TeddTokens.InkMuted,
            fontSize = 12.sp,
            modifier = Modifier.padding(top = 4.dp),
        )
    }
}

@Composable
private fun LastValuedCard(lastValued: String?, modifier: Modifier = Modifier) {
    val days = lastValued?.let { iso ->
        val a = TeddDates.daysFromIso(iso); val b = TeddDates.daysFromIso(TeddDates.todayIso)
        if (a != null && b != null) (b - a).toLong() else null
    }
    TeddCard(modifier = modifier.fillMaxWidth(), padding = PaddingValues(18.dp)) {
        Text("DERNIER CONSTAT", style = TeddType.Overline, color = TeddTokens.InkMuted)
        Text(
            text = when {
                days == null -> "—"
                days == 0L -> "aujourd'hui"
                days == 1L -> "hier"
                else -> "il y a $days j"
            },
            style = TeddType.Numeral.copy(fontSize = 26.sp, lineHeight = 32.sp),
            color = TeddTokens.Ink,
            modifier = Modifier.padding(top = 6.dp),
        )
        Text(
            text = lastValued?.let { TeddDates.formatShort(it) ?: it } ?: "aucune valeur constatée",
            color = TeddTokens.InkMuted,
            fontSize = 12.sp,
            modifier = Modifier.padding(top = 4.dp),
        )
    }
}

@Composable
private fun AssetRow(
    asset: Asset,
    valuationCount: Int,
    hideBalance: Boolean,
    onEdit: () -> Unit,
    onValue: () -> Unit,
    onDelete: () -> Unit,
) {
    val kind = AssetKind.of(asset.kind)
    val color = runCatching { parseFinHex(kind.hex) }.getOrElse { TeddTokens.LimeStrong }
    val gain = assetGain(asset)
    TeddCard(modifier = Modifier.fillMaxWidth(), padding = PaddingValues(14.dp)) {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(12.dp)) {
            Box(
                modifier = Modifier.size(34.dp).clip(RoundedCornerShape(TeddTokens.RSm)).background(color.copy(alpha = 0.13f), RoundedCornerShape(TeddTokens.RSm)),
                contentAlignment = Alignment.Center,
            ) { TeddIcon(name = kind.icon, size = 15.dp, color = color, strokeWidth = 2.dp) }
            Column(modifier = Modifier.weight(1f)) {
                Text(asset.name, style = TeddType.Heading, color = TeddTokens.Ink)
                Text(
                    text = buildString {
                        append(kind.label)
                        asset.acquired_date?.takeIf { it.isNotBlank() }?.let { append(" · acquis en ${it.take(4)}") }
                        asset.purchase_price?.let { append(" · ${fmtMoney(it, asset.currency, compact = true)}") }
                        if (valuationCount > 1) append(" · $valuationCount constats")
                    },
                    color = TeddTokens.InkMuted,
                    fontSize = 11.5.sp,
                )
            }
            Column(horizontalAlignment = Alignment.End) {
                Text(
                    text = when {
                        hideBalance -> "••••"
                        asset.value_ == null -> "—"
                        else -> fmtMoney(asset.value_, asset.currency, compact = false)
                    },
                    style = TeddType.Numeral.copy(fontSize = 17.sp, lineHeight = 21.sp),
                    color = if (asset.value_ == null) TeddTokens.InkMuted else TeddTokens.Ink,
                )
                if (asset.value_ == null) {
                    Text("jamais constaté", color = TeddTokens.InkMuted, fontSize = 11.sp)
                } else if (gain != null && !hideBalance) {
                    Text(
                        text = (if (gain >= 0) "+" else "−") + fmtMoney(if (gain < 0) -gain else gain, asset.currency, compact = true),
                        style = TeddType.Mono,
                        color = if (gain >= 0) TeddTokens.Success else TeddTokens.Warning,
                        fontSize = 11.5.sp,
                    )
                }
            }
            TeddIconButton(onClick = onValue, size = 30.dp) {
                TeddIcon(name = "arrow-up", size = 13.dp, color = TeddTokens.LimeStrong, strokeWidth = 2.dp)
            }
            TeddIconButton(onClick = onEdit, size = 30.dp) { TeddIcon(name = "edit", size = 13.dp, color = TeddTokens.InkMuted) }
            TeddIconButton(onClick = onDelete, size = 30.dp) { TeddIcon(name = "trash", size = 13.dp, color = TeddTokens.InkMuted) }
        }
    }
}

/** Création et modification d'un bien. */
@Composable
internal fun AssetEditor(
    existing: Asset?,
    defaultCurrency: String,
    onClose: () -> Unit,
    onSave: (name: String, kind: String, currency: String, value: Long?, purchasePrice: Long?, acquired: String?, note: String?) -> Unit,
) {
    var name by remember(existing) { mutableStateOf(existing?.name ?: "") }
    var kind by remember(existing) { mutableStateOf(AssetKind.of(existing?.kind)) }
    var value by remember(existing) { mutableStateOf(existing?.value_?.toString() ?: "") }
    var price by remember(existing) { mutableStateOf(existing?.purchase_price?.toString() ?: "") }
    var acquired by remember(existing) { mutableStateOf(existing?.acquired_date ?: "") }
    var note by remember(existing) { mutableStateOf(existing?.note ?: "") }
    val currency = existing?.currency ?: defaultCurrency

    Dialog(onDismissRequest = onClose, properties = DialogProperties(usePlatformDefaultWidth = false)) {
        Column(
            modifier = Modifier
                .width(460.dp)
                .heightIn(max = 660.dp)
                .clip(RoundedCornerShape(TeddTokens.RXl))
                .background(TeddTokens.Bg, RoundedCornerShape(TeddTokens.RXl))
                .verticalScroll(rememberScrollState())
                .padding(24.dp),
            verticalArrangement = Arrangement.spacedBy(14.dp),
        ) {
            Text(if (existing == null) "Nouveau bien" else "Modifier le bien", style = TeddType.Title2, color = TeddTokens.Ink)
            MoneyField("Nom", name, "Maison de famille, terrain, or…", KeyboardType.Text) { name = it }
            Column {
                Text("NATURE", style = TeddType.Overline, color = TeddTokens.InkMuted, modifier = Modifier.padding(bottom = 6.dp))
                FlowRow(horizontalArrangement = Arrangement.spacedBy(6.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
                    AssetKind.entries.forEach { k ->
                        val on = kind == k
                        val c = runCatching { parseFinHex(k.hex) }.getOrElse { TeddTokens.LimeStrong }
                        Row(
                            modifier = Modifier
                                .clip(RoundedCornerShape(TeddTokens.RFull))
                                .background(if (on) c.copy(alpha = 0.14f) else TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RFull))
                                .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) { kind = k }
                                .padding(horizontal = 11.dp, vertical = 6.dp),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(5.dp),
                        ) {
                            TeddIcon(name = k.icon, size = 12.dp, color = if (on) c else TeddTokens.InkMuted, strokeWidth = 2.dp)
                            Text(k.label, color = if (on) c else TeddTokens.Ink, fontSize = 12.sp, fontWeight = FontWeight.SemiBold)
                        }
                    }
                }
            }
            MoneyField("Valeur actuelle ($currency)", value, "Laisser vide si tu ne la connais pas", KeyboardType.Number) { value = it.filter { c -> c.isDigit() } }
            MoneyField("Prix payé ($currency)", price, "Pour calculer la plus-value", KeyboardType.Number) { price = it.filter { c -> c.isDigit() } }
            TeddDateField(label = "Acquis le", value = acquired, onValueChange = { acquired = it }, placeholder = "Date d'acquisition")
            MoneyField("Note", note, "120 g, parcelle de 500 m²…", KeyboardType.Text) { note = it }
            Spacer(modifier = Modifier.height(2.dp))
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                Spacer(modifier = Modifier.weight(1f))
                TeddButton(text = "Annuler", onClick = onClose, kind = TeddButtonKind.Secondary, size = TeddButtonSize.Md)
                TeddButton(
                    text = if (existing == null) "Ajouter" else "Enregistrer",
                    onClick = {
                        onSave(
                            name.trim(), kind.key, currency, value.toLongOrNull(), price.toLongOrNull(),
                            acquired.ifBlank { null }, note.ifBlank { null },
                        )
                    },
                    kind = TeddButtonKind.Primary,
                    size = TeddButtonSize.Md,
                    enabled = name.isNotBlank(),
                )
            }
        }
    }
}

/** Constater une nouvelle valeur, à une date choisie. */
@Composable
internal fun AssetValuationDialog(
    asset: Asset,
    history: List<Asset_valuation>,
    onClose: () -> Unit,
    onSave: (value: Long, date: String, note: String?) -> Unit,
) {
    var value by remember(asset.id) { mutableStateOf(asset.value_?.toString() ?: "") }
    var date by remember(asset.id) { mutableStateOf(TeddDates.todayIso) }
    var note by remember(asset.id) { mutableStateOf("") }

    Dialog(onDismissRequest = onClose, properties = DialogProperties(usePlatformDefaultWidth = false)) {
        Column(
            modifier = Modifier
                .width(TeddSize.SidePanel)
                .heightIn(max = 620.dp)
                .clip(RoundedCornerShape(TeddTokens.RXl))
                .background(TeddTokens.Bg, RoundedCornerShape(TeddTokens.RXl))
                .verticalScroll(rememberScrollState())
                .padding(24.dp),
            verticalArrangement = Arrangement.spacedBy(14.dp),
        ) {
            Text("Constater la valeur", style = TeddType.Title2, color = TeddTokens.Ink)
            Text(asset.name, color = TeddTokens.InkMuted, style = TeddType.BodyLg)
            MoneyField("Valeur (${asset.currency})", value, "Ce que ça vaut aujourd'hui", KeyboardType.Number) { value = it.filter { c -> c.isDigit() } }
            TeddDateField(label = "Date du constat", value = date, onValueChange = { date = it }, clearable = false)
            MoneyField("Note", note, "Estimation agence, cours du jour…", KeyboardType.Text) { note = it }
            if (history.isNotEmpty()) {
                Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
                    Text("HISTORIQUE", style = TeddType.Overline, color = TeddTokens.InkMuted)
                    history.take(8).forEach { v ->
                        Row(modifier = Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                            Text(
                                text = TeddDates.formatShort(v.date, withYear = true) ?: v.date,
                                color = TeddTokens.InkMuted,
                                fontSize = 12.sp,
                                modifier = Modifier.weight(1f),
                            )
                            Text(fmtMoney(v.value_, asset.currency, compact = false), style = TeddType.Mono, color = TeddTokens.Ink)
                        }
                    }
                }
            }
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                Spacer(modifier = Modifier.weight(1f))
                TeddButton(text = "Annuler", onClick = onClose, kind = TeddButtonKind.Secondary, size = TeddButtonSize.Md)
                TeddButton(
                    text = "Constater",
                    onClick = { value.toLongOrNull()?.let { onSave(it, date, note.ifBlank { null }) } },
                    kind = TeddButtonKind.Primary,
                    size = TeddButtonSize.Md,
                    enabled = value.toLongOrNull() != null,
                )
            }
        }
    }
}

@Composable
private fun MoneyField(label: String, value: String, placeholder: String, keyboard: KeyboardType, onChange: (String) -> Unit) {
    Column {
        Text(label.uppercase(), style = TeddType.Overline, color = TeddTokens.InkMuted, modifier = Modifier.padding(bottom = 6.dp))
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(44.dp)
                .clip(RoundedCornerShape(TeddTokens.RSm))
                .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RSm))
                .padding(horizontal = 12.dp),
            contentAlignment = Alignment.CenterStart,
        ) {
            BasicTextField(
                value = value,
                onValueChange = onChange,
                singleLine = true,
                keyboardOptions = KeyboardOptions(keyboardType = keyboard),
                textStyle = (if (keyboard == KeyboardType.Number) TeddType.Mono else TeddType.Body).copy(color = TeddTokens.Ink, fontSize = 14.sp),
                cursorBrush = SolidColor(TeddTokens.LimeStrong),
                modifier = Modifier.fillMaxWidth(),
                decorationBox = { inner ->
                    if (value.isEmpty()) Text(placeholder, color = TeddTokens.InkSubtle, fontSize = 14.sp)
                    inner()
                },
            )
        }
    }
}
